/**
 * saveGlb — the last inch of the export door: bytes → the user's disk, in TWO
 * PHASES. The File System Access picker demands TRANSIENT USER ACTIVATION —
 * the browser honors it only within seconds of the click, and a long await
 * (the 8K texture derive takes ~12s on a mastered character) spends it. The
 * one-phase shape asked AFTER serializing, so big exports silently fell
 * through to the anchor and landed in Downloads while small ones got the
 * dialog — the 2026-08-10 "intermittent save dialog" disease. Now the door
 * asks WHERE at click time (pickSaveTarget) and writes the bytes whenever
 * they are ready (writeSaveTarget); a cancel costs nothing because no
 * serialize has happened yet.
 *
 * GENERALISED OVER WHAT IS SAVED (ADR-032 D5, Codex P2 #137): the picker's
 * filter and the blob's MIME come from a `SaveKind`, so the Document door
 * saves `.harry.json` through the same two phases and the same zero-byte
 * verification the GLB door earned. The default is still the GLB kind — the
 * file keeps its name because the export doors are its first tenants.
 *
 * v2's lesson kept: a CANCELLED picker is not a save — the caller must not
 * report "exported" on 'cancelled'. Anchor fallback when the browser has no
 * picker (or refuses it for a real reason): silent save, no failure mode
 * short of the browser blocking downloads entirely.
 * DOM only, no engine. Depends on: nothing. Depended on by: main's export and Document doors.
 */

interface SaveWritable {
  write(b: Blob): Promise<void>;
  close(): Promise<void>;
}

interface SaveHandle {
  createWritable(): Promise<SaveWritable>;
  /** Read back what actually landed — the only way to know the write took. */
  getFile?: () => Promise<{ size: number }>;
}

interface SavePickerWindow {
  showSaveFilePicker?: (options: {
    suggestedName: string;
    types: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<SaveHandle>;
}

/** What a save target is for: the picker's filter and the blob's MIME. */
export interface SaveKind {
  readonly description: string;
  readonly mime: string;
  /** Leading dot, as the picker wants them. */
  readonly extensions: readonly string[];
}

export const GLB_SAVE: SaveKind = { description: 'Binary glTF', mime: 'model/gltf-binary', extensions: ['.glb'] };
/**
 * The Document as JSON. The filter is `.json`, not `.harry.json`: the
 * suggested NAME carries the double extension, the picker filter only needs
 * to admit it, and a two-dot filter is the kind of edge some pickers refuse.
 */
export const DOCUMENT_SAVE: SaveKind = { description: 'Animator Document', mime: 'application/json', extensions: ['.json'] };

export type SaveTarget = { kind: 'picker'; handle: SaveHandle } | { kind: 'anchor' } | { kind: 'cancelled' };

/**
 * Phase 1 — call at CLICK, before any serialize/derive work, while the
 * activation is fresh. 'anchor' when the browser offers no picker or refuses
 * it for any reason other than the user cancelling.
 */
export async function pickSaveTarget(suggestedName: string, kind: SaveKind = GLB_SAVE): Promise<SaveTarget> {
  const picker = (window as SavePickerWindow).showSaveFilePicker;
  if (picker === undefined) return { kind: 'anchor' };
  try {
    const handle = await picker({
      suggestedName,
      types: [{ description: kind.description, accept: { [kind.mime]: [...kind.extensions] } }],
    });
    return { kind: 'picker', handle };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return { kind: 'cancelled' };
    return { kind: 'anchor' };
  }
}

/**
 * Phase 2 — whenever the bytes are ready. The picker path writes to the
 * handle granted at click time; the anchor path downloads under `filename`.
 *
 * A HANDLE CAN GO BAD BETWEEN THE PHASES, and the export is minutes of work
 * (Codex P2, #88): permission revoked, the destination unmounted, the write
 * or close rejected. The one-phase saveGlb caught the whole picker path and
 * fell through to the anchor, and splitting the phases must not lose that —
 * so a failed handle write falls back here too, and the person still gets
 * their file. Returns WHERE the bytes actually landed, because "saved to the
 * place you chose" and "saved to Downloads instead" are different sentences
 * and the status line must not tell the second as the first.
 */
export async function writeSaveTarget(
  target: Exclude<SaveTarget, { kind: 'cancelled' }>,
  bytes: ArrayBuffer,
  filename: string,
  mime: string = GLB_SAVE.mime,
): Promise<'picker' | 'anchor'> {
  const blob = new Blob([bytes], { type: mime });
  if (target.kind === 'picker') {
    try {
      // createWritable() TRUNCATES the destination the instant it is called.
      // Everything after this point is happening to a file that is already
      // empty, which is why the failure below is not a near-miss: it leaves a
      // ZERO-BYTE file where the person's export used to be, indistinguishable
      // from a successful save until they try to open it.
      //
      // MEASURED (Allen, 2026-08-26): two of five Atlantian exports landed at
      // 0 bytes in a OneDrive-synced folder while three succeeded, and the
      // failure was SILENT — an empty catch, and a status line that said
      // "exported". The import refusal he eventually reported
      // ("Invalid typed array length: 4" out of GLTFLoader) was the honest
      // downstream complaint about an empty file.
      const writable = await target.handle.createWritable();
      await writable.write(blob);
      await writable.close();
      // VERIFY. A resolved close() is not proof: a sync layer between us and
      // the disk can accept every call and still leave nothing behind.
      const landed = await target.handle.getFile?.();
      if (landed !== undefined && landed.size !== bytes.byteLength) {
        throw new Error(
          `the chosen destination holds ${landed.size} bytes, not ${bytes.byteLength} — the write did not take`,
        );
      }
      return 'picker';
    } catch (err) {
      // NEVER SILENT. This was an empty catch, so a destination that ate the
      // bytes looked exactly like one that kept them (§8: a swallowed failure
      // is indistinguishable from a broken window). The anchor still runs —
      // the person must not lose finished work — but the reason is on the
      // record, and the caller reports 'anchor', which its status line already
      // renders as "chosen location refused — downloaded instead".
      console.error('[animator-v3] the chosen destination refused the write — falling back to Downloads', err);
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  } finally {
    URL.revokeObjectURL(url);
  }
  return 'anchor';
}

/** `${label}_${clip|anims}_${YYYY-MM-DD}.glb`, v2's convention, sanitized. */
export function exportFilename(label: string, clipNames: readonly string[]): string {
  const clean = (s: string): string => s.replace(/[^a-z0-9_-]/gi, '_');
  const date = new Date().toISOString().slice(0, 10);
  const middle = clipNames.length === 1 ? clean(clipNames[0]!) : 'anims';
  return `${clean(label)}_${middle}_${date}.glb`;
}
